// client/src/components/event/EventHubDashboard.jsx
//
// Per-event command center. One call to GET /:tenant/events/:eventId/hub
// (server/routes/eventRoutes.js) assembles tasks, ticket sales, check-in,
// budget vs actual, vendors, files and recent activity; each card links to
// the feature page that owns it.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

export default function EventHubDashboard() {
  const { tenant, eventId } = useParams();
  const navigate = useNavigate();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${baseUrl}/api/events/${tenant}/events/${eventId}/hub`);
      setHub(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load event hub');
    } finally {
      setLoading(false);
    }
  }, [tenant, eventId]);

  useEffect(() => { load(); }, [load]);

  const footer = (
    <AppFooter
      buttons={[
        { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(-1) },
        { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenant}/dashboard`) },
      ]}
    />
  );

  if (loading) return <div className="p-6 text-sm opacity-70" style={{ paddingBottom: 72 }}>Loading…{footer}</div>;
  if (error) return <div className="p-6 text-sm text-red-600" style={{ paddingBottom: 72 }}>{error}{footer}</div>;
  if (!hub) return null;

  const go = (path) => () => navigate(`/${tenant}/${path}`);
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  const t = hub.tasks;
  const tk = hub.tickets;
  const ci = hub.checkin;
  const b = hub.budget;
  const vendorCount = Object.values(hub.vendors || {}).reduce((s, n) => s + n, 0);

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-5" style={{ paddingBottom: 72 }}>
      <div>
        <h1 className="text-2xl font-semibold">{hub.event.name}</h1>
        <p className="text-sm opacity-60">
          {hub.event.startdate ? new Date(hub.event.startdate).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'No date set'}
          {' · '}{hub.event.item_count} item{hub.event.item_count === 1 ? '' : 's'}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card title="Tasks" onClick={go('tasks')}>
          <Big>{t.open}</Big><Sub>open{t.overdue ? ` · ${t.overdue} overdue` : ''}</Sub>
          <Sub>{t.completed}/{t.total} done</Sub>
        </Card>

        <Card title="Ticket sales" onClick={go(`${eventId}/orders`)}>
          <Big>{tk.totalSold}</Big><Sub>sold · {money(tk.net)} net</Sub>
          {tk.refunded > 0 && <Sub>{money(tk.refunded)} refunded</Sub>}
        </Card>

        <Card title="Check-in" onClick={go(`${eventId}/checkin`)}>
          <Big>{ci.total ? Math.round((ci.checkedIn / ci.total) * 100) : 0}%</Big>
          <Sub>{ci.checkedIn}/{ci.total} checked in</Sub>
          {hub.waitlist.waiting > 0 && <Sub>{hub.waitlist.waiting} on waitlist</Sub>}
        </Card>

        <Card title="Budget" onClick={go(`${eventId}/budget`)}>
          <Big className={b.projectedNet >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
            {money(b.projectedNet)}
          </Big>
          <Sub>projected net</Sub>
          <Sub>{money(b.actualExpense)} spent / {money(b.estimatedExpense)} est.</Sub>
        </Card>

        <Card title="Vendors" onClick={go(`${eventId}/vendors`)}>
          <Big>{vendorCount}</Big>
          <Sub>{hub.vendors.booked || 0} booked · {hub.vendors.paid || 0} paid</Sub>
        </Card>

        <Card title="Analytics" onClick={go(`${eventId}/ticketing-analytics`)}>
          <Big>{tk.orderCount}</Big><Sub>orders · {money(tk.gross)} gross</Sub>
          <Sub>{money(tk.fees)} platform fees</Sub>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <section className="rounded-lg border border-black/10 dark:border-white/10 p-4">
          <h3 className="font-semibold text-sm mb-2">Team</h3>
          {(hub.collaborators || []).length === 0 ? (
            <p className="text-sm opacity-60">No external collaborators.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {hub.collaborators.map((c, i) => (
                <li key={`${c.companyname}-${c.role}-${i}`} className="flex justify-between">
                  <span>{c.companyname}</span><span className="opacity-60">{c.role}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 text-xs opacity-60">{hub.files} file{hub.files === 1 ? '' : 's'} attached</div>
        </section>

        <section className="rounded-lg border border-black/10 dark:border-white/10 p-4">
          <h3 className="font-semibold text-sm mb-2">Recent activity</h3>
          {(hub.activity || []).length === 0 ? (
            <p className="text-sm opacity-60">Nothing logged yet.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {hub.activity.map((a, i) => (
                <li key={`${a.created_on}-${i}`} className="flex justify-between gap-2">
                  <span className="truncate">{a.type}{a.entity_name ? ` · ${a.entity_name}` : ''}</span>
                  <span className="opacity-50 whitespace-nowrap">{new Date(a.created_on).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="flex flex-wrap gap-2 text-sm">
        <button type="button" onClick={go(`${eventId}/eventDetail`)} className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15">Event details</button>
        <button type="button" onClick={go('calendar')} className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15">Calendar feed</button>
      </div>

      {footer}
    </div>
  );
}

function Card({ title, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="text-left rounded-lg border border-black/10 dark:border-white/10 p-3 hover:bg-black/5 dark:hover:bg-white/5"
    >
      <div className="text-xs uppercase tracking-wide opacity-60 mb-1">{title}</div>
      {children}
    </button>
  );
}
function Big({ children, className = '' }) {
  return <div className={`text-2xl font-semibold leading-tight ${className}`}>{children}</div>;
}
function Sub({ children }) {
  return <div className="text-xs opacity-60">{children}</div>;
}
